// src/sim/stamina/stamina.ts — P3 per-tick stamina drain/regen + depletion event (D-28).
//
// Invariants:
//   - Pure: returns { next: Entity; events: GameEvent[] }; never mutates input.
//   - Stamina always clamped to [P3_STAMINA_MIN, P3_STAMINA_MAX].
//   - stamina-depleted event emits ONCE on the prev>0 → next<=0 boundary crossing.
//     prev=0 AND next=0 emits NO event (already depleted). Regen from 0 back up
//     also emits NO event (no boundary crossing).
//
// Per D-28 priority:
//   - intent.actions.sprint === true AND prev > 0  → drain `profile.sprintDrainRate * dt`.
//     Note: sprint drains regardless of |intent.move|; the player is trying to sprint.
//   - else if |intent.move| > 0                    → regen `profile.staminaRegenMoving * dt`.
//   - else (idle)                                  → regen `profile.staminaRegenIdle * dt`.
//
// Source: .planning/phases/03-core-sim-primitives/03-CONTEXT.md D-28.

import type { Entity } from '../../shared/types/world';
import type { Intent } from '../../shared/types/intent';
import type { GameEvent } from '../../shared/types/events';
import type { CharacterProfile } from '../characters/character-profile';
import { P3_STAMINA_MIN, P3_STAMINA_MAX } from './constants';

export interface StaminaResult {
  next: Entity;
  events: GameEvent[];
}

export function applyStamina(
  entity: Entity,
  intent: Intent,
  dt: number,
  profile: CharacterProfile,
  currentTick: number,
): StaminaResult {
  const prev = entity.stamina;
  let next = prev;

  const moving = intent.move.x !== 0 || intent.move.y !== 0;

  if (intent.actions.sprint) {
    // Sprint intent has priority over regen: even if stamina is already 0 the
    // user is still trying to sprint, so we must NOT regen under a held sprint.
    // Drain applies only while prev > 0 (D-28 "sprint only applies if stamina > 0").
    if (prev > P3_STAMINA_MIN) {
      next = prev - profile.sprintDrainRate * dt;
    }
    // else: stamina stays at prev (= 0); no regen path taken.
  } else if (moving) {
    next = prev + profile.staminaRegenMoving * dt;
  } else {
    // Idle regen is higher than moving regen per D-28 ("regens faster when not moving at all").
    next = prev + profile.staminaRegenIdle * dt;
  }

  if (next < P3_STAMINA_MIN) next = P3_STAMINA_MIN;
  if (next > P3_STAMINA_MAX) next = P3_STAMINA_MAX;

  const events: GameEvent[] = [];
  // Boundary: prev was ABOVE the floor but next is AT (or below clamp to) the floor.
  // prev=0 and next=0 does NOT satisfy prev > 0, so no event (correctly suppressed).
  if (prev > P3_STAMINA_MIN && next <= P3_STAMINA_MIN) {
    events.push({ type: 'stamina-depleted', entityId: entity.id, tick: currentTick });
  }

  if (next === prev) {
    // Pitfall 11: skip the per-tick allocation when stamina is already at its
    // resting boundary and neither drain nor regen moved it (e.g. stamina=0 +
    // sprint intent with no stamina left to burn).
    return { next: entity, events };
  }
  return { next: { ...entity, stamina: next }, events };
}
